import React, { useEffect, useState, useCallback } from 'react';
import { getVersionCheck } from './versionCheck';
import { track as trackEvent } from '../../utils/analytics';
import './UpdateNudgeBanner.scss';

// Soft "a newer MixGoat is available" nudge. Driven by force-upgrade-service's
// per-platform `latestVersion` + `notes` (see its README, "Soft nudge"). It is
// deliberately NOT a modal and NOT a block: a slim dismissible card, remembered
// per version so each release nags at most once. The hard block for genuinely
// broken versions is ForceUpgradeScreen (minimumVersion / buggyVersions).
//
// Desktop-only by construction: `version:check` is an Electron IPC, so on
// web/native getVersionCheck() resolves null and nothing renders.

const DOWNLOAD_FALLBACK = 'https://mixgoat.com/download';
const DISMISS_KEY_PREFIX = 'mg:update-nudge:dismissed:';

function isDismissed(version) {
    try { return !!localStorage.getItem(DISMISS_KEY_PREFIX + version); } catch { return false; }
}
function markDismissed(version) {
    try { localStorage.setItem(DISMISS_KEY_PREFIX + version, String(Date.now())); } catch { /* private mode etc. */ }
}

const DownloadIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const UpdateNudgeBanner = () => {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getVersionCheck().then((result) => {
            if (cancelled || !result) return;
            // The hard screen owns forced upgrades — don't double up.
            if (result.forceUpgrade) return;
            if (!result.updateAvailable || !result.latestVersion) return;
            if (isDismissed(result.latestVersion)) return;
            setInfo(result);
            trackEvent('update_nudge_shown', {
                from_version: result.version || null,
                latest_version: result.latestVersion,
                has_notes: !!result.notes,
            });
        });
        return () => { cancelled = true; };
    }, []);

    const dismiss = useCallback((how) => {
        if (!info) return;
        markDismissed(info.latestVersion);
        trackEvent('update_nudge_dismissed', {
            from_version: info.version || null,
            latest_version: info.latestVersion,
            how,
        });
        setInfo(null);
    }, [info]);

    const download = useCallback(() => {
        if (!info) return;
        const url = info.downloadUrl || DOWNLOAD_FALLBACK;
        trackEvent('update_nudge_clicked', {
            from_version: info.version || null,
            latest_version: info.latestVersion,
        });
        if (window.electron?.openUrl) {
            window.electron.openUrl(url);
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        // Clicking Download counts as handled — don't keep nagging this session
        // or the next launch of the same (old) version.
        markDismissed(info.latestVersion);
        setInfo(null);
    }, [info]);

    if (!info) return null;

    return (
        <div className="update-nudge" role="status" aria-live="polite">
            <div className="update-nudge__body">
                <div className="update-nudge__title">
                    MixGoat {info.latestVersion} is available
                </div>
                <div className="update-nudge__text">
                    {info.notes ? info.notes : `You're on ${info.version || 'an older version'}.`}
                </div>
            </div>
            <div className="update-nudge__actions">
                <button type="button" className="update-nudge__btn update-nudge__btn--primary" onClick={download}>
                    <DownloadIcon />
                    Download
                </button>
                <button type="button" className="update-nudge__btn" onClick={() => dismiss('later')}>
                    Later
                </button>
            </div>
            <button type="button" className="update-nudge__close" aria-label="Dismiss" title="Dismiss" onClick={() => dismiss('close')}>
                <CloseIcon />
            </button>
        </div>
    );
};

export default UpdateNudgeBanner;
